/* global React, Icon, Button, Input, EmptyState, PlummeI18n, t */
// ============================================================
// PLUMME — Support > Articles
// Browse, search and read every help article inline. Read-only use of
// helpArticles() (help-articles.jsx); the drawer reader and the global search
// keep working as before. Search/grouping logic: services/supportArticleSearch.js.
// Open article <-> address (?artikkel=<id>) is owned by the app root through
// useSupportRouteState (services/supportRouteState.js).
// ============================================================
(function (global) {
  const { useState, useRef, useEffect, useMemo } = React;

  // Built at call time: t() must never run in a module-level constant.
  const SUPPORT_PAGES = () => [
    { value: 'tickets', label: t('support.page.tickets') },
    { value: 'articles', label: t('support.page.articles') },
  ];

  // Switcher between the two Support sub-pages. The sidebar sub-list only exists on
  // desktop, so this is how both pages stay reachable on tablet and mobile.
  function SupportSubNav({ value, onChange }) {
    return <div className="seg soft support-subnav" role="group" aria-label={t('support.pages.label')}>
      {SUPPORT_PAGES().map((page) => <button type="button" key={page.value} className={value === page.value ? 'active' : ''} aria-pressed={value === page.value} onClick={() => onChange?.(page.value)}>{page.label}</button>)}
    </div>;
  }

  // Singular for exactly one article, plural for 0 and n (same rule in no and en).
  const countLabel = (count) => t(count === 1 ? 'support.articles.count.one' : 'support.articles.count.other', { count });

  // Plain clicks open in the page; modified clicks keep the browser behavior.
  const linkClick = (go) => (global.navClick ? global.navClick(go) : (event) => { event.preventDefault(); go(); });

  function ArticleRow({ article, showCategory, onOpen }) {
    const href = global.PlummeAppRoute?.urlForSupportArticle?.(article.id, { businessId: global.PlummeBackend?.activeBusinessId || '' });
    return <li>
      <a className="sa-row" href={href} data-article-link={article.id} onClick={linkClick(() => onOpen(article.id))}>
        <span className="sa-row-main">
          <span className="sa-row-title">{article.title}</span>
          <span className="sa-row-lead">{article.lead}</span>
          <span className="sa-row-meta">{showCategory && <React.Fragment><span>{article.category}</span><span aria-hidden="true">·</span></React.Fragment>}<span>{article.read}</span></span>
        </span>
        <Icon name="chevRight" size={18} />
      </a>
    </li>;
  }

  function ArticleReader({ article, headingRef, onBack, onGo, setSubPage }) {
    const routeLabel = article.route ? t(PlummeI18n.keyFor('nav', article.route, 'route')) : '';
    return <div className="support-articles sa-reader">
      <div className="sa-reader-bar">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}><Icon name="arrowLeft" size={16} />{t('support.articles.back')}</button>
        <SupportSubNav value="articles" onChange={setSubPage} />
      </div>
      <article className="card sa-article" aria-labelledby="sa-article-title">
        <header className="sa-article-head">
          <div className="sa-eyebrow"><Icon name="help" size={14} />{t('ha.eyebrow', { category: article.category })}</div>
          <h2 id="sa-article-title" ref={headingRef} tabIndex={-1} className="t-h1 sa-article-title">{article.title}</h2>
          <div className="sa-meta"><Icon name="clock" size={14} />{article.read}</div>
          <p className="sa-lead">{article.lead}</p>
        </header>
        {global.SupportArticleBody && <global.SupportArticleBody article={article} />}
        {article.route && <footer className="sa-foot">
          <Button icon="arrowRight" onClick={onGo}>{t('ha.goTo', { page: routeLabel })}</Button>
        </footer>}
      </article>
    </div>;
  }

  function SupportArticlesPage({ articleId, setArticleId, setSubPage }) {
    const search = global.PlummeSupportArticleSearch;
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('');
    const headingRef = useRef(null);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const lastOpenedRef = useRef('');
    const firstRunRef = useRef(true);

    // Rebuilt only when the locale changes or its catalog lands (useLocaleVersion),
    // not on every keystroke.
    const localeVersion = global.useLocaleVersion ? global.useLocaleVersion() : 0;
    const registry = useMemo(() => (global.helpArticles ? global.helpArticles() : {}), [localeVersion, PlummeI18n.getLocale?.()]);
    const found = search.searchArticles(registry, query);
    const summaries = search.categorySummaries(found);
    // A category filter that is no longer in the results (a query excluded it, or the
    // locale changed its label) is dropped instead of leaving an empty list.
    const categoryPresent = !category || summaries.some((summary) => summary.category === category);
    const activeCategory = categoryPresent ? category : '';
    const visible = search.filterByCategory(found, activeCategory);
    const active = search.findArticle(registry, articleId);
    const filtering = Boolean(query.trim() || activeCategory);

    // An id from the address that is not in the registry just shows the list. The
    // address is replaced, not pushed, so Back never returns to the invalid link. Not before
    // the Firestore help store has settled: a DB-only id is unknown until its list lands.
    const helpStore = global.PlummeHelpStore;
    const settled = helpStore?.isSettled ? helpStore.isSettled() : true;
    useEffect(() => { if (articleId && !active && settled) setArticleId?.('', { replace: true }); }, [articleId, Boolean(active), settled]);

    useEffect(() => { if (!categoryPresent) setCategory(''); }, [categoryPresent]);

    // Focus follows the view: the heading when an article opens, the link that was
    // opened (or the search box) when returning to the list.
    useEffect(() => {
      if (firstRunRef.current) { firstRunRef.current = false; return; }
      if (active) {
        lastOpenedRef.current = active.id;
        headingRef.current?.focus();
        document.querySelector('.main')?.scrollTo?.(0, 0);
        return;
      }
      const link = listRef.current?.querySelector(`[data-article-link="${lastOpenedRef.current}"]`);
      (link || searchRef.current?.querySelector('input'))?.focus();
    }, [active?.id]);

    const open = (id) => setArticleId?.(id);
    if (active) {
      const go = () => {
        if (active.route === 'support') setSubPage?.('tickets');
        else global.plummeNav?.(active.route);
      };
      return <ArticleReader article={active} headingRef={headingRef} onBack={() => setArticleId?.('')} onGo={go} setSubPage={setSubPage} />;
    }

    const clearFilters = () => { setQuery(''); setCategory(''); };
    const groups = search.groupArticlesByCategory(visible);
    return <div className="support-articles" ref={listRef}>
      <div className="support-page-header">
        <h2 className="t-h1">{t('support.title')}</h2>
        <div className="support-toolbar"><SupportSubNav value="articles" onChange={setSubPage} /></div>
      </div>
      <div className="sa-search" ref={searchRef}>
        <Input icon="search" type="search" value={query} autoComplete="off" onChange={(event) => setQuery(event.target.value)}
          placeholder={t('support.articles.search.placeholder')} aria-label={t('support.articles.search.label')} />
      </div>
      {summaries.length > 0 && <section className="sa-categories" aria-labelledby="sa-categories-title">
        <h3 id="sa-categories-title" className="sa-section-title">{t('support.articles.categories')}</h3>
        <div className="sa-cat-grid">
          {summaries.map((summary) => <button type="button" key={summary.category} className={'sa-cat' + (activeCategory === summary.category ? ' active' : '')} aria-pressed={activeCategory === summary.category}
            onClick={() => setCategory(activeCategory === summary.category ? '' : summary.category)}>
            <span className="sa-cat-name">{summary.category}</span>
            <span className="sa-cat-count">{countLabel(summary.count)}</span>
          </button>)}
        </div>
      </section>}
      <p className="sa-status" role="status" aria-live="polite">{filtering ? t('support.articles.results', { count: visible.length }) : ''}</p>
      {visible.length === 0
        ? <div className="card"><EmptyState noVisual title={t('support.articles.empty.title')} text={t('support.articles.empty.text')} action={filtering ? <Button variant="secondary" onClick={clearFilters}>{t('support.articles.clear')}</Button> : null} /></div>
        : query.trim()
          ? <section className="card sa-list" aria-labelledby="sa-results-title">
            <h3 id="sa-results-title" className="sa-section-title">{t('support.articles.resultsTitle')}</h3>
            <ul className="sa-rows">{visible.map((article) => <ArticleRow key={article.id} article={article} showCategory onOpen={open} />)}</ul>
          </section>
          : groups.map((group, index) => <section key={group.category} className="card sa-list" aria-labelledby={'sa-group-' + index}>
            <h3 id={'sa-group-' + index} className="sa-section-title">{group.category}<span className="sa-section-count">{countLabel(group.articles.length)}</span></h3>
            <ul className="sa-rows">{group.articles.map((article) => <ArticleRow key={article.id} article={article} onOpen={open} />)}</ul>
          </section>)}
    </div>;
  }

  global.SUPPORT_PAGES = SUPPORT_PAGES;
  global.SupportSubNav = SupportSubNav;
  global.SupportArticlesPage = SupportArticlesPage;
})(typeof window !== 'undefined' ? window : globalThis);
